import { AnimatePresence, motion } from 'framer-motion'
import { abbreviateNumber } from 'js-abbreviation-number'
import ClaravineLogo from 'images/c_logo_white_green.svg'
import { AvailableIntegration } from './components/available-integration'
import { Graph } from './components/graph-animations'
import { IntegrationCard } from './components/integration-card'
import { SkeletonCard } from './components/skeleton-card'
import { useIntegrationsReport } from './hooks/useIntegrationsReport'
import { t } from '@lingui/core/macro'

export const IntegrationsGraph = () => {
  const { submissions, outbounds, isLoading, unusedIntegrations, latestSubmissionDate } =
    useIntegrationsReport()

  const noIntegrationData =
    !isLoading && !submissions?.withData?.length && !outbounds?.withData?.length

  return (
    <div className="h-full mb-[50px]">
      {!noIntegrationData && (
        <Graph>
          <Graph.Connector
            centerItem={
              <Graph.CenterItem>
                <div>
                  <h2 className="text-[10px] text-cv-blue-400">{t`From`}</h2>
                  <p className="font-bold text-cv-blue-500">{latestSubmissionDate}</p>
                </div>
                <div className="w-[124px] h-[70px] rounded-[20px] bg-[#2F4F4F] flex justify-center items-center">
                  <ClaravineLogo />
                </div>
                <div>
                  <h2 className="text-[10px] text-cv-blue-400">{t`To`}</h2>
                  <p className="font-bold text-cv-blue-500">{t`Present`}</p>
                </div>
              </Graph.CenterItem>
            }
            topLeaves={
              isLoading
                ? Array.from({ length: 3 }).map((_, index) => <SkeletonCard key={index} />)
                : submissions?.withData?.map(integrationSummary => (
                    <IntegrationCard key={integrationSummary.integrationId}>
                      <IntegrationCard.Title>
                        <img
                          className="max-w-[20px]"
                          src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${integrationSummary.integrationSlug}.png`}
                        />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {integrationSummary.integrationName}
                        </span>
                      </IntegrationCard.Title>
                      <IntegrationCard.Container>
                        <IntegrationCard.Content>
                          <span className="text-[32px] leading-tight">
                            {abbreviateNumber(integrationSummary.totalRowCount)}
                          </span>
                          <span className="text-[10px]">ROWS</span>
                        </IntegrationCard.Content>
                      </IntegrationCard.Container>
                    </IntegrationCard>
                  )) || []
            }
            bottomLeaves={
              isLoading
                ? Array.from({ length: 3 }).map((_, index) => (
                    <SkeletonCard key={`outbound-${index}`} />
                  ))
                : outbounds?.withData?.map(integrationSummary => (
                    <IntegrationCard key={integrationSummary.integrationId + 1}>
                      <IntegrationCard.Container>
                        <IntegrationCard.Content>
                          <span className="text-[32px] leading-tight">
                            {abbreviateNumber(integrationSummary.totalRowCount)}
                          </span>
                          <span className="text-[10px]">ROWS</span>
                        </IntegrationCard.Content>
                      </IntegrationCard.Container>
                      <IntegrationCard.Title>
                        <img
                          className="max-w-[20px]"
                          src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${integrationSummary.integrationSlug}.png`}
                        />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {integrationSummary.integrationName}
                        </span>
                      </IntegrationCard.Title>
                    </IntegrationCard>
                  )) || []
            }
          />
        </Graph>
      )}
      {(unusedIntegrations?.length || 0) > 0 && (
        <div className="max-w-[1000px] m-auto mt-[65px]">
          <AnimatePresence>
            {isLoading ? null : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-center text-cv-blue-600 text-[16px] pb-4 font-roboto">
                  <a
                    href="https://support.claravine.com/hc/en-us/categories/360003386631"
                    target="_blank"
                    className="underline"
                    rel="noreferrer"
                  >
                    {t`More available integrations`}
                  </a>
                </h2>
                <div className="flex flex-wrap gap-[15px] mt-[20px] justify-center">
                  {unusedIntegrations?.map(integration => (
                    <AvailableIntegration key={integration.id}>
                      <AvailableIntegration.Icon>
                        <img
                          src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${integration.slug}.png`}
                          alt={integration.name}
                        />
                      </AvailableIntegration.Icon>
                      <AvailableIntegration.Name>{integration.name}</AvailableIntegration.Name>
                    </AvailableIntegration>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
